import type { FeedLog } from '../types/production'
import { toLocalCalendarDayKey } from './dateNormalization'
import { farmDayKey } from './farmTime'
import { resolveCanonicalLotKey } from './lotIdentity'

// Findings #141: a feeding lands in Base de datos on the (farm day, lot) row.
// Office feedings (Alimento) never created that row, and crew feedings only
// do since #256 — so a day with feedings and no other record had nowhere to
// show its kg (B1, 2026-10-07). This lists those (day, lot) pairs, only for
// lots that still exist in the unit (a deleted lot gets no new row).

export type FeedDayWithoutRow = { dayKey: string; lotId: string; lotKey: string }

export const findFeedDaysWithoutRow = (args: {
  rows: ReadonlyArray<{ date?: string | null; lot?: string | null; lotId?: string | null }>
  feedLogs: ReadonlyArray<Pick<FeedLog, 'recordedAt' | 'lotId' | 'lot'>>
  lots: ReadonlyArray<{ id: string; lotKey?: string | null }>
  timeZone: string
}): FeedDayWithoutRow[] => {
  const lotById = new Map(args.lots.map((lot) => [lot.id, lot] as const))
  const covered = new Set<string>()
  for (const row of args.rows) {
    const day = toLocalCalendarDayKey(String(row.date ?? ''), args.timeZone)
    if (!day) continue
    const lotId = String(row.lotId ?? '').trim()
    if (lotId) covered.add(`${day}::id:${lotId}`)
    const label = resolveCanonicalLotKey(String(row.lot ?? ''))
    if (label && label !== 'unknown-lot') covered.add(`${day}::label:${label}`)
  }

  const missing = new Map<string, FeedDayWithoutRow>()
  for (const log of args.feedLogs) {
    const lotId = String(log.lotId ?? '').trim()
    const lot = lotId ? lotById.get(lotId) : undefined
    const lotKey = String(lot?.lotKey ?? '').trim()
    if (!lot || !lotKey) continue
    const day = farmDayKey(log.recordedAt, args.timeZone)
    if (!day) continue
    if (covered.has(`${day}::id:${lotId}`) || covered.has(`${day}::label:${resolveCanonicalLotKey(lotKey)}`)) continue
    missing.set(`${day}::${lotId}`, { dayKey: day, lotId, lotKey })
  }
  return Array.from(missing.values()).sort((a, b) => a.dayKey.localeCompare(b.dayKey))
}
